import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

export interface SandboxPreview {
  feature_key: string;
  feature_name: string;
  role: string;
  pta_id: string;
  pta_name: string;
  expires_at: number;
}

interface Ctx {
  preview: SandboxPreview | null;
  startPreview: (p: Omit<SandboxPreview, "expires_at">) => void;
  exitPreview: () => void;
}

const STORAGE_KEY = "sandbox_preview";
const TTL_MS = 60 * 60 * 1000; // 60 min

const SandboxPreviewCtx = createContext<Ctx>({
  preview: null,
  startPreview: () => {},
  exitPreview: () => {},
});

export function SandboxPreviewProvider({ children }: { children: ReactNode }) {
  const [preview, setPreview] = useState<SandboxPreview | null>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SandboxPreview;
      if (parsed.expires_at < Date.now()) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!preview) return;
    const ms = preview.expires_at - Date.now();
    if (ms <= 0) {
      setPreview(null);
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const t = setTimeout(() => {
      setPreview(null);
      sessionStorage.removeItem(STORAGE_KEY);
    }, ms);
    return () => clearTimeout(t);
  }, [preview]);

  const startPreview = useCallback((p: Omit<SandboxPreview, "expires_at">) => {
    const full: SandboxPreview = { ...p, expires_at: Date.now() + TTL_MS };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(full));
    setPreview(full);
  }, []);

  const exitPreview = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPreview(null);
  }, []);

  const value = useMemo(() => ({ preview, startPreview, exitPreview }), [preview, startPreview, exitPreview]);
  return <SandboxPreviewCtx.Provider value={value}>{children}</SandboxPreviewCtx.Provider>;
}

export function useSandboxPreview() {
  return useContext(SandboxPreviewCtx);
}
